import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConsentRecord,
  ConsentTemplate,
  CreateConsentRecord,
  UpsertConsentTemplate,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS-13 Feature 4 — CONSULTATION / CONSENT records (LRU-07). TanStack Query over the generated client,
 * mirroring data/reviews.ts (unwrap, body `as never`, onSuccess invalidate) and data/loyalty.ts (a
 * per-client view gated on the clientId).
 *
 * This is a PII/LIABILITY feature with NO money — so unlike the loyalty/voucher/billing reads it is NOT
 * money-adjacent: the reads get a NORMAL staleTime (the query-client default, 30s) and there is no need to
 * keep them out of any offline replica for money-honesty reasons (these keys are simply absent from
 * PERSISTED_PREFIXES because consent PII is never mirrored to an offline replica, not because it's money).
 *
 * IMMUTABILITY: a record is APPEND-ONLY + IMMUTABLE-ONCE-SIGNED — there is no edit/delete hook. A
 * correction is a NEW capture, never a mutation. The server FREEZES the snapshotTitle/snapshotBody from the
 * template at sign time (the liability surface, reproducible verbatim) and, for a patch_test, sets
 * validUntil + PROJECTS clients.patch_test_last_at/due_at. So after a capture we invalidate the client's
 * consents AND the clients tree (the patch-test due/last dates the booking prompt + client list read).
 *
 * TEMPLATES are VERSIONED-NOT-MUTATED: a manager PATCH that changes the WORDING creates a NEW version
 * server-side (never altering a row with signed records); `active:false` deactivates without a version
 * bump. Either write re-reads the whole templates tree.
 */

// ── Reads ──────────────────────────────────────────────────────────────────────────

/**
 * One client's signed consent/consultation history (newest signed first). Gated on a clientId so a closed
 * detail pane doesn't fire. The list endpoint returns 200 with an empty array when there's nothing yet, so
 * there is no 404 to map; a genuine error surfaces via `isError`, which the panel renders honestly. Normal
 * staleTime (not money) — a capture invalidates it explicitly.
 */
export function useClientConsents(clientId: string | null) {
  return useQuery({
    queryKey: clientId ? qk.clientConsents(clientId) : ['client-consents', 'none'],
    enabled: Boolean(clientId),
    queryFn: async (): Promise<ConsentRecord[]> =>
      unwrap(
        await api.GET('/v1/clients/{clientId}/consents', {
          params: { path: { clientId: clientId! } },
        }),
      ),
  });
}

/**
 * One consent record including its FROZEN verbatim snapshot (snapshotTitle/snapshotBody) — the durable
 * liability evidence reproduced exactly as signed. Gated on the id so a closed viewer doesn't fire. An
 * immutable row, so this caches happily at the default staleTime.
 */
export function useConsentRecord(id: string | null) {
  return useQuery({
    queryKey: id ? qk.consentRecord(id) : ['consent-record', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<ConsentRecord> =>
      unwrap(await api.GET('/v1/consents/{id}', { params: { path: { id: id! } } })),
  });
}

/**
 * The salon's consent templates. `active` filters to the live/active versions (the ones a staff member can
 * capture against) — the editor passes `undefined` to see deactivated/superseded rows too. A list read
 * allowed for any staff role (RLS-scoped); creating/editing is manager+ on the API.
 */
export function useConsentTemplates(active?: boolean) {
  return useQuery({
    queryKey: qk.consentTemplates(active ?? null),
    queryFn: async (): Promise<ConsentTemplate[]> =>
      unwrap(
        await api.GET('/v1/consent-templates', {
          params: { query: active === undefined ? {} : { active } },
        }),
      ),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────────

/**
 * Capture a signed consent record (staff-level). The server freezes the snapshot from the template, sets
 * signedVia='console_staff' + signedByStaffId + signedIp + signedAt=now, and for a patch_test derives
 * validUntil and PROJECTS the client's patch_test_last_at/due_at. So onSuccess we invalidate this client's
 * consents AND the clients tree (the patch-test badge on the list + detail reads those projected dates).
 */
export function useCaptureConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clientId: string;
      body: CreateConsentRecord;
    }): Promise<ConsentRecord> =>
      unwrap(
        await api.POST('/v1/clients/{clientId}/consents', {
          params: { path: { clientId: input.clientId } },
          body: input.body as never,
        }),
      ),
    onSuccess: (_record, input) => {
      void qc.invalidateQueries({ queryKey: qk.clientConsents(input.clientId) });
      // A patch_test capture re-projects clients.patch_test_due_at/last_at — refresh the client list +
      // detail so the "patch test due" badge reflects the new validity window.
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: qk.client(input.clientId) });
    },
  });
}

/**
 * Create (POST, no id) or version/deactivate (PATCH, id) a consent template — manager on the API. A
 * wording change (title/body/fields/validityDays) creates a NEW version server-side, never mutating a row
 * that has signed records; `active:false` deactivates without a version bump. Either path re-reads the
 * whole templates tree (the active list a capture flow reads, and the full editor list, both move).
 */
export function useUpsertConsentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      body: UpsertConsentTemplate;
    }): Promise<ConsentTemplate> => {
      if (input.id) {
        return unwrap(
          await api.PATCH('/v1/consent-templates/{id}', {
            params: { path: { id: input.id } },
            body: input.body as never,
          }),
        );
      }
      return unwrap(await api.POST('/v1/consent-templates', { body: input.body as never }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['consent-templates'] });
    },
  });
}
